import { User, LogOut, Sun, Moon, Monitor, ChevronsUpDown, Languages } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "@/ui/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/ui/components/ui/sidebar";
import { useSession } from "@/auth/contexts/auth-context.tsx";
import { useTheme } from "@/ui/contexts/theme-provider";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FALLBACK_LOCALE, SUPPORTED_LOCALES } from "@/shared/lib/i18n/resources.ts";

/**
 * User menu pinned to the sidebar footer (bottom-left). Shows a user icon +
 * "Account" label when expanded, collapses to just the icon on the rail. Opens
 * My Account / Theme / Log out.
 */
const UserMenu = () => {
  const { t } = useTranslation("common");
  const { signOut } = useSession();
  const { theme, setTheme } = useTheme();
  const { isMobile } = useSidebar();
  const { i18n } = useTranslation();
  const activeLocale =
    SUPPORTED_LOCALES.find((l) => l.code === i18n.resolvedLanguage) ??
    SUPPORTED_LOCALES.find((l) => l.code === FALLBACK_LOCALE)!;
  const navigate = useNavigate();

  const handleLogout = () => {
    signOut();
    navigate("/login");
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                <User className="h-4 w-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{t("userMenu.account")}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel>{t("userMenu.myAccount")}</DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Theme Submenu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Sun className="mr-2 h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="mr-2 h-4 w-4 absolute rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span className="ml-6">{t("userMenu.theme")}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    <Sun className="mr-2 h-4 w-4" />
                    <span>{t("userMenu.light")}</span>
                    {theme === "light" && <span className="ml-auto">✓</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    <Moon className="mr-2 h-4 w-4" />
                    <span>{t("userMenu.dark")}</span>
                    {theme === "dark" && <span className="ml-auto">✓</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    <Monitor className="mr-2 h-4 w-4" />
                    <span>{t("userMenu.system")}</span>
                    {theme === "system" && <span className="ml-auto">✓</span>}
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            {/* Language Submenu — mirrors Theme above. Lives here rather than
                in the top bar so the shell's chrome stays task controls only. */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Languages className="mr-2 h-4 w-4" />
                <span aria-hidden="true" className="mr-1.5 text-base leading-none">
                  {activeLocale.flag}
                </span>
                <span className="truncate">{activeLocale.label}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {SUPPORTED_LOCALES.map((locale) => (
                    <DropdownMenuItem
                      key={locale.code}
                      onClick={() => i18n.changeLanguage(locale.code)}
                    >
                      <span aria-hidden="true" className="mr-2 text-base leading-none">
                        {locale.flag}
                      </span>
                      <span>{locale.label}</span>
                      {locale.code === activeLocale.code && <span className="ml-auto">✓</span>}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            {/* Logout */}
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t("userMenu.logOut")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default UserMenu;
